import { Injectable, inject } from "@angular/core";
import { HttpBackend, HttpClient, HttpParams } from "@angular/common/http";
import { map } from "rxjs/operators";
import { firstValueFrom } from "rxjs";
import { NgxHateoasClientConfigurationService } from "@lagoshny/ngx-hateoas-client";

export interface ClientConfig {
  appName: string;
  serviceUrl: string;
  messageUrl: string;
  bookPastDue: boolean;
  maxPlanningWindow: number;
  disableAnimation: boolean;
  enableTimesheetCodes: boolean;
  disableResourceGroups?: boolean;
  enableFloatingBooking?: boolean;
  restrictAlertsByPlannerTeams?: boolean;
  disableTicketRecall?: boolean;
  disableTwoPersonTicketRecall?: boolean;
  appointmentStartOnly?: boolean;
  azureConfig: AzureConfig;
  urmServices: UrmServiceConfig;
  maps: MapsConfig;
  ticketColours: TicketColoursConfig;
  applicationInsights: ApplicationInsightsConfig;
  partitioning?: PartitioningConfig;
}

export interface PartitioningConfig {
  splitHour: number;
  splitDays: PartitioningSplitDays;
}

export interface PartitioningSplitDays {
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
}

export interface AzureConfig {
  tenantId: string;
  clientId: string;
  publicUrl: string;
  // OAuth response mode for the redirect login. Omitted/undefined uses MSAL's
  // default ("fragment", i.e. callback arrives as #code=...). Set to "query" to
  // have Entra return the code in the query string (?code=...) instead — used
  // to test around the Chrome 149 Login Detection hang on fragment callbacks.
  responseMode?: "fragment" | "query";
  adminRole: string;
  plannerRole: string;
  viewerRole: string;
  apiConfig: ApiConfig[];
  fineGrainedPermissions: FineGrainedPermissions;
}

export interface FineGrainedPermissions {
  enableAllowResourceLocking: boolean;
  allowResourceLocking: string;
  resourceLocking: FineGrainedPermission;
  auditViewer: FineGrainedPermission;
  forceBooking: FineGrainedPermission;
  priorityReservation: FineGrainedPermission;
}

export interface FineGrainedPermission {
  enabled: boolean;
  roles: string[];
}

export interface ApiConfig {
  origin: string;
  scopes: string[];
}

export interface UrmServiceConfig {
  enabled: boolean;
  simulate?: boolean;
  serviceUrl: string;
  launchUrl: LaunchUrlConfig;
  activityLog: ActivityLogConfig;
}

export interface ActivityLogConfig {
  enabled: boolean;
  cutoffHours: number;
  refreshIntervalSeconds: number;
  rules: ActivityLogRules;
}

export interface ActivityLogRule {
  showStatus: boolean;
  showTimesheetCode: boolean;
  displayAs: string;
  highlightRow: boolean;
}

export interface ActivityLogRules {
  Travelling: ActivityLogRule;
  AssessingJob: ActivityLogRule;
  Working: ActivityLogRule;
  Recording: ActivityLogRule;
  Pause: ActivityLogRule;
  AwaitingJob: ActivityLogRule;
  Unavailable: ActivityLogRule;
}

export interface LaunchUrlConfig {
  enableLaunchUrl: boolean;
  jobLaunchUrl: string;
}

export interface MapsConfig {
  enabled: boolean;
  tileUrl: string;
  bingKey: string;
  imagerySet: string;
}

/*
  "applicationInsights":{
    "mode": "force",
    "instrumentationKey": "8acd7fbd-899a-484f-b261-12423eb659ee"
  },
 */

// mode one of force, optional, disabled
export interface ApplicationInsightsConfig {
  mode: string;
  instrumentationKey: string;
}

export interface TicketColoursConfig {
  status: TicketStatusColoursConfig;
  timings: TicketTimingsColourConfig;
}

export interface TicketStatusColoursConfig {
  PLANNING: ColourConfig;
  PINNED: ColourConfig;
  DESPATCHED: ColourConfig;
  ENROUTE: ColourConfig;
  ARRIVED: ColourConfig;
  STARTED: ColourConfig;
  PAUSED: ColourConfig;
  COMPLETE: ColourConfig;
  PLANNINGISSUE: ColourConfig;
}

export interface ColourConfig {
  foreground: string;
  background: string;
}

export interface TicketTimingsColourConfig {
  travel: string;
  wait: string;
  work: string;
  textColour: string;
}

@Injectable({
  providedIn: "root"
})
export class ClientConfigService {
  private httpHandler = inject(HttpBackend);

  private http: HttpClient;
  private _hateoasConfigService;

  constructor() {
    const httpHandler = this.httpHandler;
    const hateoasConfigService = inject(NgxHateoasClientConfigurationService);

    // console.log("Creating client config");
    this.http = new HttpClient(httpHandler);
    this._hateoasConfigService = hateoasConfigService;
  }

  private _config: ClientConfig;

  get config(): ClientConfig {
    // console.log("Config request made");
    return this._config;
  }

  init(): Promise<ClientConfig> {
    // console.log("Running initialiser");
    return new Promise<ClientConfig>((resolve, reject) => {
      // console.log("Preparing to call client config service");
      const params = new HttpParams().set("ngsw-bypass", true);
      firstValueFrom(
        this.http
          .get("config\\client-config.json", { params })
          .pipe(map((res) => res))
      )
        .then((value) => {
          // console.log("Http call returned", value);
          this._config = <ClientConfig>value;
          if (this._config.bookPastDue === undefined) {
            this._config.bookPastDue = true;
          }
          if (this._config.maxPlanningWindow == undefined) {
            this._config.maxPlanningWindow = 0;
          }
          if (this._config.disableResourceGroups === undefined) {
            this._config.disableResourceGroups = false;
          }
          if (this._config.enableFloatingBooking === undefined) {
            this._config.enableFloatingBooking = false;
          }
          if (this._config.disableTicketRecall === undefined) {
            this._config.disableTicketRecall = false;
          }
          if (this._config.disableTwoPersonTicketRecall === undefined) {
            this._config.disableTwoPersonTicketRecall = false;
          }
          if (this._config.appointmentStartOnly === undefined) {
            this._config.appointmentStartOnly = false;
          }
          if (this._config.partitioning === undefined) {
            this._config.partitioning = {
              splitHour: 7,
              splitDays: {
                monday: true,
                tuesday: true,
                wednesday: true,
                thursday: true,
                friday: true,
                saturday: true,
                sunday: true
              }
            };
          }

          if (
            !this._config.azureConfig.fineGrainedPermissions.resourceLocking &&
            this._config.azureConfig.fineGrainedPermissions.allowResourceLocking
          ) {
            this._config.azureConfig.fineGrainedPermissions.resourceLocking = {
              enabled: true,
              roles: [
                this._config.azureConfig.fineGrainedPermissions
                  .allowResourceLocking
              ]
            };
          }

          if (!this._config.azureConfig.fineGrainedPermissions.auditViewer) {
            this._config.azureConfig.fineGrainedPermissions.auditViewer = {
              enabled: false,
              roles: []
            };
          }
          if (
            !this._config.azureConfig.fineGrainedPermissions.resourceLocking
          ) {
            this._config.azureConfig.fineGrainedPermissions.resourceLocking = {
              enabled: false,
              roles: []
            };
          }
          if (!this._config.azureConfig.fineGrainedPermissions.forceBooking) {
            this._config.azureConfig.fineGrainedPermissions.forceBooking = {
              enabled: false,
              roles: []
            };
          }
          if (
            !this._config.azureConfig.fineGrainedPermissions.priorityReservation
          ) {
            this._config.azureConfig.fineGrainedPermissions.priorityReservation =
              {
                enabled: false,
                roles: []
              };
          }

          // Validate responseMode from runtime JSON so a typo / wrong casing
          // can never reach MSAL. Anything other than the allowed literals is
          // dropped to undefined, falling back to MSAL's default ("fragment").
          const responseMode = this._config.azureConfig.responseMode;
          if (
            responseMode !== undefined &&
            responseMode !== "fragment" &&
            responseMode !== "query"
          ) {
            console.warn(
              `[ClientConfig] Ignoring invalid azureConfig.responseMode "${responseMode}" — expected "fragment" or "query". Falling back to MSAL default.`
            );
            this._config.azureConfig.responseMode = undefined;
          }

          resolve(this._config);
        })
        .catch((reason) => {
          // console.error("Unable to get config", reason);
          reject(reason);
        });
    });
  }
}
